import { useEffect, useMemo, useRef, useState } from 'react';

import { AuthProviderProps } from './_types';

import { API_BASE_URL } from '~/common/configs/environment.config';
import { logger } from '~/libs/logger/';
import { useAuthService } from '~/services/agent/login';

/** Auth Context Hooks */
export const useHooks = (props: AuthProviderProps) => {
  const { client_id, client_secret } = props;

  const { execute, loading } = useAuthService();

  const [token, setToken] = useState<string>();
  const [tokenError, setTokenError] = useState<boolean>(false);

  /** Check authentication to get token */
  const getToken = async () => {
    try {
      const response = await execute({
        agent_id: client_id,
        agent_secret: client_secret,
      });

      if (response?.access_token) {
        setTokenError(false);
        return response.access_token;
      }

      setTokenError(true);
      return undefined;
    } catch (error) {
      logger.error('Login failed:', error);
      setTokenError(true);
      return undefined;
    }
  };

  const decodeToken = (token: string) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload;
    } catch (error) {
      console.error('Failed to decode token:', error);
      return null;
    }
  };

  const refreshTimeout = useRef<number>(0);

  useEffect(() => {
    const fetchToken = async () => {
      const fetchedToken = await getToken();
      if (fetchedToken) {
        setToken(fetchedToken);

        // Save token to localStorage for SSE requests
        try {
          localStorage.setItem('auth_token', fetchedToken);
        } catch (error) {
          logger.error('Failed to save token to localStorage:', error);
        }

        const payload = decodeToken(fetchedToken);
        if (payload && payload.exp) {
          const expiresIn = payload.exp * 1000 - Date.now();
          const refreshTime = expiresIn - 60000; // Refresh 1 minute before expiration

          if (refreshTime > 0) {
            refreshTimeout.current = window.setTimeout(fetchToken, refreshTime);
          }
        }
      }
    };

    fetchToken();

    return () => {
      if (refreshTimeout.current) {
        window.clearTimeout(refreshTimeout.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client_id, client_secret]);

  return useMemo(
    () => ({
      apiURL: API_BASE_URL,
      token,
      tokenError,
      external_user: props.external_user,
      loading,
    }),
    [loading, props.external_user, token, tokenError],
  );
};
